import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ImpressumScreen from './ImpressumScreen';

const { FiDownload, FiRefreshCw, FiUser, FiPhone } = FiIcons;

const ResultScreen = ({ results, onRestart }) => {
  const { scores, percentages, careers, interpretation } = results;
  const [showImpressum, setShowImpressum] = useState(false);

  if (showImpressum) {
    return <ImpressumScreen onBack={() => setShowImpressum(false)} />;
  }

  const pieData = [
    { name: 'Dominanz (D)', value: percentages.D, color: '#ef4444' },
    { name: 'Initiative (I)', value: percentages.I, color: '#f59e0b' },
    { name: 'Stetigkeit (S)', value: percentages.S, color: '#10b981' },
    { name: 'Gewissenhaftigkeit (C)', value: percentages.C, color: '#3b82f6' }
  ];

  const barData = [
    { name: 'D', value: percentages.D, color: '#ef4444' },
    { name: 'I', value: percentages.I, color: '#f59e0b' },
    { name: 'S', value: percentages.S, color: '#10b981' },
    { name: 'C', value: percentages.C, color: '#3b82f6' }
  ];

  const typeDescriptions = {
    D: {
      title: 'Dominanz (Rot)',
      description: 'Führungsstark, ergebnisorientiert, entscheidungsfreudig'
    },
    I: {
      title: 'Initiative (Gelb)', 
      description: 'Kommunikativ, enthusiastisch, überzeugend'
    },
    S: {
      title: 'Stetigkeit (Grün)',
      description: 'Teamorientiert, geduldig, zuverlässig'
    },
    C: {
      title: 'Gewissenhaftigkeit (Blau)',
      description: 'Analytisch, qualitätsbewusst, systematisch'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 pb-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiUser} className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Ihr DISC-Profil</h1>
          <p className="text-xl text-gray-600">Ihre persönliche Persönlichkeitsanalyse</p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Pie Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl shadow-xl p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Verteilung der Typen</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-2`} style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm font-medium text-gray-700">{item.name}: {item.value}%</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Bar Chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-3xl shadow-xl p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Detaillierte Werte</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `${value}%`} />
                <Bar dataKey="value" radius={8}>
                  {barData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>

        {/* Type Descriptions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-3xl shadow-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Ihre Persönlichkeitstypen</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(typeDescriptions).map(([type, info]) => (
              <div key={type} className="text-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                  type === 'D' ? 'bg-red-100' :
                  type === 'I' ? 'bg-yellow-100' :
                  type === 'S' ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  <span className={`text-2xl font-bold ${
                    type === 'D' ? 'text-red-600' :
                    type === 'I' ? 'text-yellow-600' :
                    type === 'S' ? 'text-green-600' : 'text-blue-600'
                  }`}>{type}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{info.description}</p>
                <div className={`text-2xl font-bold ${
                  type === 'D' ? 'text-red-600' :
                  type === 'I' ? 'text-yellow-600' :
                  type === 'S' ? 'text-green-600' : 'text-blue-600'
                }`}>
                  {percentages[type]}%
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Career Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-white rounded-3xl shadow-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Passende Berufsfelder</h2>
          <p className="text-gray-600 text-center mb-8">
            Basierend auf Ihrem DISC-Profil empfehlen wir folgende Berufe:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {careers.map((career, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 text-center"
              >
                <span className="font-medium text-blue-800">{career}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Interpretation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-white rounded-3xl shadow-xl p-8 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Interpretation Ihres Profils</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            {interpretation}
          </div>
        </motion.div>

        {/* Professional Test & Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl shadow-xl p-8 mb-8 text-center text-white"
        >
          <h3 className="text-xl font-bold mb-4">Interesse an einer professionellen Analyse?</h3>
          <p className="text-slate-300 mb-6">
            Falls Sie einen professionellen, zertifizierten Test durchführen möchten, stehen wir Ihnen gerne zur Verfügung.
          </p>
          <a 
            href="tel:06921939559"
            className="inline-flex items-center space-x-3 bg-white text-slate-900 font-bold py-3 px-8 rounded-xl hover:bg-slate-100 transition-colors duration-300"
          >
            <SafeIcon icon={FiPhone} className="w-5 h-5" />
            <span>069-21939559</span>
          </a>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="text-center"
        >
          <button
            onClick={onRestart}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold py-4 px-8 rounded-2xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-lg inline-flex items-center space-x-3"
          >
            <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
            <span>Test wiederholen</span>
          </button>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-4">
              Dieser Test wurde vollständig anonym durchgeführt. Ihre Daten wurden nicht gespeichert.
            </p>
            <button
              onClick={() => setShowImpressum(true)}
              className="text-sm text-gray-500 hover:text-blue-600 underline transition-colors"
            >
              Impressum
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultScreen;